import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TabBar } from './tabs/TabBar';
import { SceneTab } from './tabs/SceneTab';
import { WorkspaceTabDescriptor, WorkspaceTabInstance } from './tabs/types';
import '../styles/workspace-shell.css';
import { TasksPanel } from '../panels/TasksPanel';
import { FilesPanel } from '../panels/FilesPanel';
import { ChatPanel } from '../panels/ChatPanel';
import { BeeMonitorPanel } from '../panels/BeeMonitor';
import { HiveCtlBridge } from '../panels/HiveCtlBridge';
import { SettingsPanel } from '../panels/SettingsPanel';
import { ApprovalsTray } from '../components/controls/ApprovalsTray';
import { Toasts } from '../components/system/Toasts';

export interface WorkspaceShellProps {
    SideNav?: React.ReactNode;
    initialTabs?: WorkspaceTabDescriptor[];
}

const defaultTabs: WorkspaceTabDescriptor[] = [
    {
        sceneId: 'hivecore.tasks',
        title: 'Tasks',
        profile: 'desktop',
        closable: false,
        render: () => <TasksPanel />,
    },
];

function createInstanceId(prefix: string) {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

function bootstrapTabs(descriptors: WorkspaceTabDescriptor[]): WorkspaceTabInstance[] {
    return descriptors.map((descriptor, index) => ({
        instanceId:
            descriptor.closable === false && descriptor.sceneId
                ? descriptor.sceneId
                : createInstanceId(`tab${index}`),
        closable: descriptor.closable ?? true,
        ...descriptor,
    }));
}

export const WorkspaceShell: React.FC<WorkspaceShellProps> = ({ SideNav, initialTabs }) => {
    const seededTabs = useMemo(
        () => bootstrapTabs(initialTabs?.length ? initialTabs : defaultTabs),
        [initialTabs]
    );
    const [tabs, setTabs] = useState<WorkspaceTabInstance[]>(seededTabs);
    const [activeId, setActiveId] = useState<string>(seededTabs[0]?.instanceId ?? '');

    useEffect(() => {
        setTabs(seededTabs);
        setActiveId(seededTabs[0]?.instanceId ?? '');
    }, [seededTabs]);

    const handleSelect = useCallback((id: string) => {
        setActiveId(id);
    }, []);

    const ensureActive = useCallback(
        (remaining: WorkspaceTabInstance[], removedId: string) => {
            if (remaining.length === 0) {
                setActiveId('');
                return;
            }
            if (removedId === activeId) {
                const fallback = remaining[remaining.length - 1];
                setActiveId(fallback.instanceId);
            }
        },
        [activeId]
    );

    const handleClose = useCallback(
        (id: string) => {
            setTabs(prev => {
                const target = prev.find(tab => tab.instanceId === id);
                if (!target || target.closable === false) {
                    return prev;
                }
                const next = prev.filter(tab => tab.instanceId !== id);
                ensureActive(next, id);
                return next;
            });
        },
        [ensureActive]
    );

    const addTab = useCallback((descriptor: WorkspaceTabDescriptor) => {
        const instance: WorkspaceTabInstance = {
            instanceId: createInstanceId('tab'),
            closable: descriptor.closable ?? true,
            ...descriptor,
        };
        setTabs(prev => [...prev, instance]);
        setActiveId(instance.instanceId);
    }, []);

    useEffect(() => {
        function hotkeyHandler(event: KeyboardEvent) {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                addTab({ sceneId: 'hivecore.chat', title: 'Command', render: () => <ChatPanel /> });
                return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
                event.preventDefault();
                addTab({ sceneId: 'hivecore.files', title: 'Files', render: () => <FilesPanel /> });
                return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === '[') {
                event.preventDefault();
                setTabs(prev => {
                    const idx = prev.findIndex(tab => tab.instanceId === activeId);
                    if (idx > 0) setActiveId(prev[idx - 1].instanceId);
                    return prev;
                });
                return;
            }
            if ((event.metaKey || event.ctrlKey) && event.key === ']') {
                event.preventDefault();
                setTabs(prev => {
                    const idx = prev.findIndex(tab => tab.instanceId === activeId);
                    if (idx < prev.length - 1) setActiveId(prev[idx + 1].instanceId);
                    return prev;
                });
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === '/') {
                event.preventDefault();
                document.body.classList.toggle('sidenav-hidden');
            }
        }
        window.addEventListener('keydown', hotkeyHandler);
        return () => window.removeEventListener('keydown', hotkeyHandler);
    }, [activeId, addTab]);

    const tabBarItems = useMemo(
        () =>
            tabs.map(tab => ({
                id: tab.instanceId,
                title: tab.title,
                icon: tab.icon,
                closable: tab.closable !== false,
            })),
        [tabs]
    );

    return (
        <div className="workspace-shell">
            {SideNav && <div className="workspace-shell-sidenav">{SideNav}</div>}
            <div className="workspace-shell-main">
                <div className="workspace-status-bar">
                    <span className="badge env">DEV</span>
                    <span className="status-dot online" />
                    <span>Live</span>
                    <span className="jwt-exp">JWT: fresh</span>
                    <button
                        onClick={() =>
                            addTab({
                                sceneId: 'hivecore.bees',
                                title: 'Bee Monitor',
                                render: () => <BeeMonitorPanel />,
                            })
                        }
                    >
                        Bees
                    </button>
                    <button
                        onClick={() =>
                            addTab({
                                sceneId: 'hivecore.hivectl',
                                title: 'HiveCtl',
                                render: () => <HiveCtlBridge />,
                            })
                        }
                    >
                        HiveCtl
                    </button>
                    <button
                        onClick={() =>
                            addTab({
                                sceneId: 'hivecore.settings',
                                title: 'Settings',
                                render: () => <SettingsPanel />,
                            })
                        }
                    >
                        Settings
                    </button>
                </div>
                <TabBar
                    items={tabBarItems}
                    activeId={activeId}
                    onSelect={handleSelect}
                    onClose={handleClose}
                    onAdd={() =>
                        addTab({
                            sceneId: 'hivecore.tasks',
                            title: 'Tasks',
                            render: () => <TasksPanel />,
                        })
                    }
                />
                <div className="workspace-shell-content">
                    {tabs.map(tab => (
                        <SceneTab
                            key={tab.instanceId}
                            tab={tab}
                            isActive={tab.instanceId === activeId}
                        />
                    ))}
                </div>
            </div>
            <aside className="workspace-shell-approvals">
                <ApprovalsTray />
            </aside>
            <Toasts />
        </div>
    );
};
